import { useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { COMPARE_MAX } from "@/lib/compareSelection";
import { COMPARE_LINES, NA } from "@/lib/compareLines";

function CompareTable({ rows, onRemove }) {
  return (
    <div className="overflow-x-auto -mx-1 px-1">
      <table data-testid="compare-table" className="w-full min-w-[480px] text-sm border-collapse">
        <caption className="sr-only">Selected courses side by side</caption>
        <thead>
          <tr>
            <th scope="col" className="w-36 text-left align-bottom p-2 text-[11px] uppercase tracking-wider text-stone-500">Course</th>
            {rows.map((r) => (
              <th key={r.degree_id} scope="col" data-testid={`compare-col-${r.degree_id}`} className="min-w-[160px] text-left align-top p-2 border-l border-stone-200">
                <Link to={`/degree/${r.degree_id}`} className="font-semibold text-stone-900 hover:underline break-words">{r.degree_name || NA}</Link>
                <div className="text-[12px] font-normal text-stone-500 break-words">{r.university_name}</div>
                <button type="button" onClick={() => onRemove(r)} data-testid={`compare-remove-${r.degree_id}`}
                  aria-label={`Remove ${r.degree_name} from comparison`}
                  className="mt-1 inline-flex items-center gap-1 text-[11px] font-normal text-stone-500 hover:text-rose-700">
                  <X className="h-3 w-3" aria-hidden="true" /> Remove
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {COMPARE_LINES.map(([label, get]) => (
            <tr key={label} className="border-t border-stone-100">
              <th scope="row" className="text-left font-normal text-stone-600 p-2">{label}</th>
              {rows.map((r) => {
                const v = get(r);
                return (
                  <td key={r.degree_id} data-testid={`compare-cell-${label.replace(/\W+/g, "-").replace(/^-|-$/g, "").toLowerCase()}-${r.degree_id}`}
                    className={`p-2 border-l border-stone-200 font-mono ${v.startsWith(NA) ? "text-stone-400" : "text-stone-900"}`}>{v}</td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CompareTray({ rows, onRemove, onClear }) {
  const [open, setOpen] = useState(false);
  if (!rows.length) return null;
  const n = rows.length;
  const fullHref = `/compare?tab=degrees&${rows.map((r, i) => `d${i + 1}=${encodeURIComponent(r.degree_id)}`).join("&")}`;
  return (
    <>
      <div role="region" aria-label="Course comparison" data-testid="compare-tray"
        className="fixed bottom-0 inset-x-0 z-40 border-t-2 border-[#4C1D95] bg-white/95 backdrop-blur px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] print:hidden">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center gap-x-3 gap-y-2">
          <span data-testid="compare-tray-count" className="text-sm text-stone-700">
            {n} of {COMPARE_MAX} courses selected{n < 2 ? " · select at least 2 to compare" : ""}
          </span>
          <div className="hidden sm:flex flex-wrap gap-1.5 min-w-0">
            {rows.map((r) => (
              <span key={r.degree_id} className="inline-flex max-w-[220px] items-center gap-1 rounded-full border border-stone-300 bg-stone-50 pl-2.5 pr-1 py-0.5 text-[12px]">
                <span className="truncate">{r.degree_name} · {r.university_name}</span>
                <button type="button" onClick={() => onRemove(r)} data-testid={`compare-tray-remove-${r.degree_id}`}
                  aria-label={`Remove ${r.degree_name} from comparison`} className="rounded-full p-0.5 hover:bg-stone-200">
                  <X className="h-3 w-3" aria-hidden="true" />
                </button>
              </span>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onClear} data-testid="compare-clear">Clear</Button>
            <Button type="button" size="sm" disabled={n < 2} onClick={() => setOpen(true)} data-testid="compare-open"
              className="bg-[#4C1D95] hover:bg-[#3b1479] text-white">
              Compare selected ({n})
            </Button>
          </div>
        </div>
      </div>
      <Dialog open={open && n >= 2} onOpenChange={setOpen}>
        <DialogContent data-testid="compare-dialog" className="bg-white max-w-5xl w-[calc(100vw-1.5rem)] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-editorial text-2xl">Compare {n} courses</DialogTitle>
            <DialogDescription>
              Figures from this Quick Check run. Comparing never changes rankings, chances or your shortlist.
            </DialogDescription>
          </DialogHeader>
          <CompareTable rows={rows} onRemove={onRemove} />
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2 text-[12px] text-stone-500">
            <span>Costs are per year in US dollars. "Not available" means we have no sourced figure.</span>
            <Link to={fullHref} data-testid="compare-full-link" className="text-[#4C1D95] underline underline-offset-4">
              Open in full course compare
            </Link>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
